import { Alert } from 'react-bootstrap';

export type ParentEntityLifecycleAccessLevel = 'readWrite' | 'readOnly' | 'none';

export type ParentEntityLifecycleBannerProps = {
  /** Label for the record being viewed (e.g. Tipper, Customer). */
  entityLabel: string;
  /** Display name of the record being viewed. */
  name?: string | null;
  entityIsDeleted?: boolean;
  entityIsEnabled?: boolean;
  parentSiteName?: string | null;
  parentSiteIsDeleted?: boolean;
  parentSiteIsEnabled?: boolean;
  accessLevel: ParentEntityLifecycleAccessLevel;
};

function formatEntitySubject(entityLabel: string, name?: string | null): string {
  const label = entityLabel.trim() || 'Record';
  const trimmedName = name?.trim();
  return trimmedName ? `${label} "${trimmedName}"` : label;
}

function formatSitePhrase(siteName?: string | null): string {
  const trimmed = siteName?.trim();
  return trimmed ? `the "${trimmed}" site` : 'the site';
}

export function buildParentEntityLifecycleMessage(props: ParentEntityLifecycleBannerProps): string | null {
  const subject = formatEntitySubject(props.entityLabel, props.name);
  const sitePhrase = formatSitePhrase(props.parentSiteName);

  if (props.parentSiteIsDeleted) {
    if (props.accessLevel === 'readOnly' || props.accessLevel === 'none') {
      return `${subject} belongs to ${sitePhrase} which has been deleted. You can view details but cannot make changes until the site is restored.`;
    }
  }

  if (props.entityIsDeleted) {
    return `${subject} has been deleted. Restore it before making changes.`;
  }

  if (props.parentSiteIsEnabled === false) {
    if (props.accessLevel === 'readOnly' || props.accessLevel === 'none') {
      return `${subject} belongs to ${sitePhrase} which is disabled. You can view details but cannot make changes. Organisation administrators can edit master data while the site is disabled.`;
    }
  }

  if (props.entityIsEnabled === false) {
    return `${subject} is disabled.`;
  }

  if (props.accessLevel === 'readOnly') {
    return `${subject} is read-only for your account on this site. Organisation administrators can make changes while the site is disabled.`;
  }

  return null;
}

export function ParentEntityLifecycleBanner(props: ParentEntityLifecycleBannerProps) {
  const message = buildParentEntityLifecycleMessage(props);
  if (!message) {
    return null;
  }

  const variant =
    props.parentSiteIsDeleted ||
    props.entityIsDeleted ||
    props.parentSiteIsEnabled === false ||
    props.entityIsEnabled === false
      ? 'warning'
      : 'info';

  return (
    <Alert variant={variant} className="mb-3">
      {message}
    </Alert>
  );
}

export function isSiteScopedFormReadOnly(
  metaDeleted: boolean,
  siteAccessLevel?: ParentEntityLifecycleAccessLevel | string | null,
): boolean {
  if (metaDeleted) {
    return true;
  }

  return siteAccessLevel !== 'readWrite';
}
